/*
 * This file contains the floorplan filters component.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import CustomDatePicker from "../../DatePicker/datePicker";
import "react-datepicker/dist/react-datepicker.css";
import Dropdown from "../../Dropdown/dropdown";
import styles from "../floorplans.module.scss";
import clsx from "clsx";
import { getSortLabel, transformData } from "./createFilterOptions";
import fetchData from "../../../utils/fetchData";
import { traceFilterEvent } from "../../../utils/tracing";

export default function FloorplanFilters({
  handleTabChange,
  activeTab,
  selectedOptions,
  setSelectedOptions,
  sort,
  setSort,
  typeListing,
  setIsUserInteraction,
  clearFilters,
  data,
}) {
  const [filterOptions, setFilterOptions] = useState(null);
  const [startDate, setStartDate] = useState(null);
 

  // For unit listing, show list and map tabs. For type listing, show grid and map tabs
  const tabList = typeListing ? ["grid"] : ["list"];

  // This effect fetches the filter options for the floorplans.
  // It fetches the data from the API and updates the filter options state accordingly.
  // If there is an error, it sets the error state.
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const response = await fetchData("/wp-json/entrata/v1/getFilters");
        const optionsData = transformData(response);
        setFilterOptions(optionsData);
      } catch (err) {
        // setError(err.message);
      }
    };

    fetchFilterData();
  }, []);

  // This effect handles making spans in the total cost description clickable
  // It finds all span elements and makes them scroll to the community cost guide section
  const handleTotalCostDescriptionClick = (e) => {
    if (e.target.closest('span.bottom-scroll-link')) {
      document.getElementById('community-cost-guide')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // This function handles the change in selected options.
  // It updates the selected options state based on the key and value.
  // If the key is "sort", it updates the sort state.
  // Otherwise, it updates the selected options state.
  const handleSelectChange = useCallback(
    (key, value) => {
      traceFilterEvent(key, typeListing);
      if (key === "sort") {
        setSort(value);
      } else {
        setSelectedOptions((prev) => ({
          ...prev,
          [key]: value,
        }));
      }
    },
    // eslint-disable-next-line
    [setSelectedOptions, typeListing]
  );

  // This constant defines the filters for the floorplans.
  // It includes the label, component type, key, and options for each filter.
  const filters = [
    {
      label: "Bedrooms",
      Component: Dropdown,
      key: "bed",
      options: filterOptions?.bedroom?.options,
    },
    {
      label: "Move-in Date",
      Component: CustomDatePicker,
      key: "moveInStartDate",
    },
    {
      label: "Max Square Footage",
      Component: Dropdown,
      key: "sqft",
      options: filterOptions?.sqFt?.options,
    },
    {
      label: "Max Price",
      Component: Dropdown,
      key: "price",
      options: filterOptions?.price?.options,
    },
    {
      label: "Sort By",
      Component: Dropdown,
      key: "sort",
      options: filterOptions?.sort?.options,
    },
  ];

  return (
    <>
 <div className={clsx(styles.floorplansFilters, "floorplans-filters")}>
      <div className={styles.tabs}>
        {tabList?.map((view) => (
          <button
            key={view}
            id={`${view}ViewBtn`}
            className={clsx(
              styles[`${view}ViewBtn`],
              activeTab === view && styles.active
            )}
            // disabled={view === "map"}
            onClick={() => handleTabChange(view)}
          >
            {view === "grid" || view === "list" ? "Floor Plan View" : `${view.charAt(0).toUpperCase() + view.slice(1)} View`}
          </button>
        ))}
           <button>
          <a href="/find-your-home/building-view/">Map View</a>
        </button>
      </div>
      {activeTab === "grid" || activeTab === "list" ? (
        <div className={styles.filters}>
          {filters.map(({ label, Component, key, options }, idx) => (
            <React.Fragment key={key}>
              <div
                className={clsx(
                  styles[`${key}Filter`],
                  styles.filter,
                  !typeListing && styles.onUnitListing
                )}
              >
                {Component === Dropdown ? (
                  <Dropdown
                    label={label}
                    options={options}
                    setSelected={handleSelectChange}
                    setIsUserInteraction={setIsUserInteraction}
                    dropdownKey={key}
                    selected={
                      key === "sort"
                        ? {
                            value: sort || "",
                            label: sort ? (getSortLabel(sort) || "Select") : "Select",
                          }
                        : {
                            value: selectedOptions[key] || "",
                            label: selectedOptions[key]
                              ? (options?.find(
                                  (opt) => opt.value === selectedOptions[key]
                                )?.label || "Select")
                              : "Select",
                          }
                    }
                  />
                ) : (
                  <Component
                    selected={
                      selectedOptions?.moveInStartDate
                        ? selectedOptions.moveInStartDate
                        : startDate
                    }
                    onChange={(date) => setStartDate(date)}
                    onDateSelected={() => traceFilterEvent("moveInStartDate", typeListing)}
                    setIsUserInteraction={setIsUserInteraction}
                    placeholderText="MM/DD/YYYY"
                    format="MM/dd/yyyy"
                    minDate={(() => {
                      const minAllowedDate = new Date(filterOptions?.minDate);
                      const currentDate = new Date();
                      return currentDate > minAllowedDate ? currentDate : minAllowedDate;
                    })()}
                    inputName="Move-In Date"
                    setSelectedOptions={setSelectedOptions}
                  />
                )}
              </div>
              {idx < filters.length - 1 && (
                <div
                  className={clsx(
                    styles.VerticalLine,
                    !typeListing && styles.onUnitListing,
                    styles[`${key}Line`]
                  )}
                ></div>
              )}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className={styles.sightmap}></div>
      )}
      <div className={styles.clearFilter}>
        <button className={styles.clearFilterBtn} onClick={clearFilters}>
          Clear Filters
        </button>
      </div>
    </div>

      {/* Engrain Fee Calculator Title and Description */}
      {data?.engrain_fee_calculator?.enabled === "1" && (
        <div className={styles.FeeCalculatorInfo}>
          <h3 className={styles.FeeCalculatorTitle}>
            {data?.engrain_fee_calculator?.title}
          </h3>
          <div 
            className={styles.FeeCalculatorDescription}
          >
            <p dangerouslySetInnerHTML={{ __html: data?.engrain_fee_calculator?.description }} />
          </div>
        </div>
      )}

    {data?.community_cost_guide?.total_cost_clarity_title && data?.community_cost_guide?.total_cost_clarity_description && (
      <div className={styles.totalCost}>
        <h3>{data.community_cost_guide.total_cost_clarity_title}</h3>
        <div 
          onClick={handleTotalCostDescriptionClick}
        
        >
        <p dangerouslySetInnerHTML={{ __html: data.community_cost_guide.total_cost_clarity_description }} />  
        </div>
      </div>
    )}
    </>
  );
}
